import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, ExternalLink, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const ShopsList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    totalResults: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 20,
    skip: 0,
    hasMore: false,
  });
  const navigate = useNavigate();

  const fetchShops = useCallback(async (searchTerm = '', page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const skip = (page - 1) * pagination.limit;
      const response = await apiService.getShops({
        q: searchTerm || undefined,
        limit: pagination.limit,
        skip,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      if (response.success) {
        setShops(response.data.shops || []);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination,
          currentPage: page,
        }));
      } else {
        setError(response.message || 'Failed to fetch shops');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch shops');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchShops(searchQuery, 1);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handlePageChange = (newPage) => {
    fetchShops(searchQuery, newPage);
  };

  const getSubscriptionBadgeStyle = (subscription) => {
    if (!subscription) return 'bg-[#334155] text-[#94A3B8] border-[#475569]';
    
    const status = subscription.subscriptionStatus || subscription;
    switch (status) {
      case 'ACTIVE':
      case 'Both':
        return 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30';
      case 'CANCELLED':
      case 'Front Desk Only':
        return 'bg-[#0EA5E9]/20 text-[#38BDF8] border-[#0EA5E9]/30';
      case 'EXPIRED':
      case 'Growth Engine Only':
        return 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/30';
      default:
        return 'bg-[#334155] text-[#94A3B8] border-[#475569]';
    }
  };

  const getWebsiteBadgeStyle = (website) => {
    if (!website) return 'bg-[#334155] text-[#64748B] border-[#475569]';
    return 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30';
  };

  const formatShopData = (shop) => ({
    id: shop._id,
    name: shop.shopName || 'N/A',
    address: shop.address || 'N/A',
    city: shop.city || 'N/A',
    state: shop.state || 'N/A',
    phoneNumber: shop.shopPhoneNumber || 'N/A',
    contactPerson: shop.contactPerson || 'N/A',
    contactNumber: shop.contactNumber || 'N/A',
    subscriptionStatus: shop.subscription?.subscriptionStatus || 'N/A',
    website: shop.websiteLink || null,
  });

  return (
    <div className="p-6 md:p-8" data-testid="shops-list-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Auto Shops
        </h1>
        <p className="text-[#94A3B8]">
          Manage and view all registered auto shops
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <Input
            type="text"
            placeholder="Search by shop name, city, or state..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1E293B] border-[#334155] text-[#E2E8F0] placeholder:text-[#64748B] focus:ring-2 focus:ring-[#2563EB] focus:border-transparent h-11"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-4 mb-6">
          <p className="text-[#F87171]">{error}</p>
          <Button 
            onClick={() => fetchShops(searchQuery, pagination.currentPage)}
            className="mt-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#334155] hover:bg-transparent">
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Shop Name</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Address</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">City</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">State</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Phone</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Contact</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Subscription</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Website</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin mx-auto" />
                    <p className="text-[#64748B] mt-2">Loading shops...</p>
                  </TableCell>
                </TableRow>
              ) : shops.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-[#64748B]">
                    {searchQuery ? 'No shops found matching your search.' : 'No shops registered yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                shops.map((rawShop) => {
                  const shop = formatShopData(rawShop);
                  return (
                    <TableRow
                      key={shop.id}
                      className="border-b border-[#334155]/50 hover:bg-[#334155]/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/shops/${shop.id}`)}
                      data-testid={`shop-row-${shop.id}`}
                    >
                      <TableCell className="font-medium text-[#E2E8F0]">
                        <div className="flex items-center gap-2">
                          {shop.name}
                          <ExternalLink className="w-3.5 h-3.5 text-[#64748B]" />
                        </div>
                      </TableCell>
                      <TableCell className="text-[#94A3B8]">{shop.address}</TableCell>
                      <TableCell className="text-[#94A3B8]">{shop.city}</TableCell>
                      <TableCell className="text-[#94A3B8]">{shop.state}</TableCell>
                      <TableCell className="text-[#94A3B8]">{shop.phoneNumber}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-[#E2E8F0] text-sm">{shop.contactPerson}</p>
                          <p className="text-[#64748B] text-xs">{shop.contactNumber}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${getSubscriptionBadgeStyle(shop.subscriptionStatus)} border`}>
                          {shop.subscriptionStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {shop.website ? (
                          <Badge variant="outline" className={`${getWebsiteBadgeStyle(shop.website)} border`}>
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-[#334155] text-[#64748B] border-[#475569]">
                            No Website
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && shops.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-[#64748B]">
            Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalResults)} of {pagination.totalResults} shops
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-[#94A3B8] text-sm px-2">
              Page {pagination.currentPage} of {pagination.totalPages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasMore}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopsList;
